import {apiEndpoint} from '@/utils/Api.ts';
import {errorMap} from '@/utils/zod.ts';
import {zodResolver} from '@hookform/resolvers/zod';
import BackIcon from '@mui/icons-material/ArrowBack';
import {Box, useTheme} from '@mui/material';
import {Grid2 as Grid} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import {RhfTextField} from 'mui-rhf-integration';
import {useSnackbar} from 'notistack';
import type * as React from 'react';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import GlenbrookLogo from '../../assets/glenbrook225.png';
import LoadingButton from '../LoadingButton';
import PaddedContainer from '../PaddedContainer';
import {type User, authManager} from './AuthManager';

const loginFormSchema = z.object({
    username: z.string(),
    password: z.string(),
});

export type LoginFormSchema = z.infer<typeof loginFormSchema>;

type Props = {
    requireAuth: boolean;
    requiredTokenLifetime: number;
    onClose: () => void;
    onAuth?: () => void;
    onExpire?: () => void;
};

const AuthHandler: React.FC<Props> = ({
    requireAuth,
    requiredTokenLifetime,
    onClose,
    onAuth,
    onExpire,
}: Props) => {
    const theme = useTheme();
    const [authenticated, setAuthenticated] = useState(false);
    const {enqueueSnackbar} = useSnackbar();

    const form = useForm<LoginFormSchema>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            username: '',
            password: '',
        },
        resolver: zodResolver(loginFormSchema, {errorMap}),
    });

    useEffect(() => {
        if (authManager.hasTokenLifetimeLeft(requiredTokenLifetime)) {
            setAuthenticated(true);
        }

        const handleAuth = () => {
            setAuthenticated(true);
            onAuth?.();
        };

        const handleExpire = () => {
            setAuthenticated(false);
            onExpire?.();
        };

        authManager.addEventListener('auth', handleAuth);
        authManager.addEventListener('expire', handleExpire);

        return () => {
            authManager.removeEventListener('auth', handleAuth);
            authManager.removeEventListener('expire', handleExpire);
        };
    }, [requiredTokenLifetime, onAuth, onExpire]);

    const handleSubmit = async () => {
        const formData = form.getValues();

        let response: Response;

        try {
            response = await fetch(new URL('/authenticate', apiEndpoint).toString(), {
                method: 'POST',
                body: JSON.stringify(formData),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        } catch (e) {
            enqueueSnackbar('Server not reachable, please try again later.', {variant: 'error'});
            onClose();
            return;
        }

        if (!response.ok) {
            if (response.status === 401) {
                return {password: 'Invalid credentials'};
            }

            enqueueSnackbar('An unknown error occurred.', {variant: 'error'});
            onClose();
            return;
        }

        const user = (await response.json()) as User;
        authManager.setUser(user);
    };

    return (
        <Dialog fullScreen open={requireAuth && !authenticated}>
            <AppBar position='static'>
                <Toolbar>
                    <IconButton edge='start' color='inherit' onClick={onClose}>
                        <BackIcon />
                    </IconButton>
                    <Typography variant='h6'>Authenticate</Typography>
                </Toolbar>
            </AppBar>

            <PaddedContainer maxWidth='sm'>
                <Box
                    sx={{
                        textAlign: 'center',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <img
                        src={GlenbrookLogo}
                        width={200}
                        height={134.3}
                        alt='Glenbrook High Schools - District 225'
                    />
                </Box>

                <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                    <Grid container spacing={2}>
                        <Grid size={12} sx={{display: 'flex', justifyContent: 'center'}}>
                            <RhfTextField
                                control={form.control}
                                name='username'
                                label='Username'
                                placeholder='Username'
                                sx={{my: 1}}
                                onChangeCapture={() => {
                                    form.clearErrors();
                                }}
                                variant='outlined'
                                required
                                // disabled={submitting}
                            />
                        </Grid>
                        <Grid size={12} sx={{display: 'flex', justifyContent: 'center'}}>
                            <RhfTextField
                                type='password'
                                control={form.control}
                                name='password'
                                label='Password'
                                placeholder='Password'
                                sx={{my: 1}}
                                onChangeCapture={() => {
                                    form.clearErrors();
                                }}
                                variant='outlined'
                                required
                                // disabled={submitting}
                            />
                        </Grid>
                        <Grid size={12} sx={{display: 'flex', justifyContent: 'center'}}>
                            <LoadingButton
                                color='secondary'
                                variant='contained'
                                // disabled={submitting}
                                type='submit'
                                // loading={submitting}
                            >
                                Sign in
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </form>
            </PaddedContainer>
        </Dialog>
    );
};

export default AuthHandler;
